import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import qrcode from 'qrcode-generator';
import './Auth.css';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

interface TOTPRegistrationResponse {
  uri: string;
  secret: string;
}

const TOTPSetup: React.FC = () => {
  const navigate = useNavigate();
  const [qrCodeSvg, setQrCodeSvg] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'register' | 'verify'>('register');

  useEffect(() => {
    startTOTPRegistration();
  }, []);

  const startTOTPRegistration = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated. Please log in.');
        return;
      }

      const response = await axios.post<TOTPRegistrationResponse>(
        `${API_BASE_URL}/auth/custom/totp/register`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      setSecret(response.data.secret);

      // Generate QR code using qrcode-generator
      const qr = qrcode(0, 'M');
      qr.addData(response.data.uri);
      qr.make();
      const svgString = qr.createSvgTag({ cellSize: 4, margin: 4 });
      setQrCodeSvg(svgString);
      setStep('verify');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to start TOTP registration';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated. Please log in.');
        return;
      }

      await axios.post(
        `${API_BASE_URL}/auth/custom/totp/verify`,
        { code: verificationCode },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      alert('Two-factor authentication enabled successfully!');
      navigate('/profile');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Invalid verification code';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(secret);
    alert('Secret copied to clipboard!');
  };

  if (loading && step === 'register') {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Setting up 2FA...</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Setup Two-Factor Authentication</h1>
          <p>Scan the QR code with your authenticator app</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {step === 'verify' && (
          <div className="totp-setup">
            <div className="qr-code-container" style={{ textAlign: 'center', marginBottom: '20px' }}>
              {qrCodeSvg && (
                <div
                  dangerouslySetInnerHTML={{ __html: qrCodeSvg }}
                  style={{ display: 'inline-block', border: '2px solid #ddd', borderRadius: '8px', padding: '10px', background: 'white' }}
                />
              )}
            </div>

            <div className="secret-container" style={{ marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Can't scan? Enter this code manually:
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <code
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '14px',
                    wordBreak: 'break-all',
                  }}
                >
                  {secret}
                </code>
                <button
                  type="button"
                  onClick={copyToClipboard}
                  style={{
                    padding: '10px 15px',
                    background: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Copy
                </button>
              </div>
            </div>

            <form onSubmit={handleVerifyCode} className="auth-form">
              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  type="text"
                  id="verificationCode"
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value.replace(/\D/g, ''));
                    setError('');
                  }}
                  placeholder="000000"
                  disabled={loading}
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  required
                />
                <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                  Enter the 6-digit code from your authenticator app
                </small>
              </div>

              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? 'Verifying...' : 'Verify and Enable 2FA'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="back-button"
                disabled={loading}
                style={{ marginTop: '10px' }}
              >
                Cancel
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default TOTPSetup;
